import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import BrandMark from "@/components/BrandMark";

export default function Unsubscribe() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState({ loading: true, email: "", already: false, done: false, err: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) {
      setState({ loading: false, email: "", already: false, done: false, err: "Missing unsubscribe token." });
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/unsubscribe-lookup?token=${encodeURIComponent(token)}`);
        if (data.valid === false || !data.email) {
          setState({ loading: false, email: "", already: false, done: false, err: "Invalid or expired link." });
          return;
        }
        setState({ loading: false, email: data.email, already: !!data.already_unsubscribed, done: false, err: "" });
      } catch (err) {
        setState({ loading: false, email: "", already: false, done: false, err: err.response?.data?.detail || "Invalid or expired link." });
      }
    })();
  }, [token]);

  const confirm = async () => {
    setBusy(true);
    try {
      await api.post("/unsubscribe", { token });
      setState((s) => ({ ...s, done: true, already: true }));
    } catch (err) {
      setState((s) => ({ ...s, err: err.response?.data?.detail || "Something went wrong. Please try again." }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-bone flex flex-col" data-testid="unsubscribe-page">
      <header className="border-b border-borderSubtle py-6 px-6 lg:px-12">
        <BrandMark size="md" asLink={true} />
      </header>
      <main className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-lg text-center">
          {state.loading && <p className="overline">Loading…</p>}

          {!state.loading && state.err && (
            <>
              <p className="overline text-oxblood mb-3">Link problem</p>
              <h1 className="font-serif text-4xl mb-4">This link isn't working.</h1>
              <p className="text-stone text-sm mb-8 leading-relaxed" data-testid="unsub-error">{state.err}</p>
              <Link
                to="/account"
                className="inline-block px-6 py-3 bg-charcoal text-bone tracking-[0.22em] text-xs uppercase rounded-full hover:bg-oxblood transition-colors"
                data-testid="unsub-account-link"
              >
                Manage subscription in Account
              </Link>
            </>
          )}

          {!state.loading && !state.err && state.done && (
            <>
              <p className="overline text-oxblood mb-3">Done</p>
              <h1 className="font-serif text-4xl md:text-5xl mb-4">You're unsubscribed.</h1>
              <p className="text-stone leading-relaxed mb-2">
                We removed <span className="font-mono text-charcoal">{state.email}</span> from the mailing list.
              </p>
              <p className="text-stone text-sm leading-relaxed mb-8">
                You'll still get order confirmations, tracking updates, and other transactional email — just no marketing.
              </p>
              <Link
                to="/shop"
                className="inline-block px-6 py-3 border border-borderSubtle text-charcoal hover:border-oxblood hover:text-oxblood tracking-[0.22em] text-xs uppercase rounded-full transition-colors"
                data-testid="unsub-shop-link"
              >
                Back to catalog
              </Link>
            </>
          )}

          {!state.loading && !state.err && !state.done && state.already && (
            <>
              <p className="overline text-stone mb-3">Already handled</p>
              <h1 className="font-serif text-4xl mb-4">You're already unsubscribed.</h1>
              <p className="text-stone leading-relaxed mb-2">
                <span className="font-mono text-charcoal">{state.email}</span> is not on the mailing list.
              </p>
              <p className="text-stone text-sm leading-relaxed mb-8">
                Want back in? Sign in and flip the toggle in Account.
              </p>
              <Link
                to="/account"
                className="inline-block px-6 py-3 bg-charcoal text-bone tracking-[0.22em] text-xs uppercase rounded-full hover:bg-oxblood transition-colors"
                data-testid="unsub-account-link"
              >
                Go to Account
              </Link>
            </>
          )}

          {!state.loading && !state.err && !state.done && !state.already && (
            <>
              <p className="overline text-oxblood mb-3">Confirm</p>
              <h1 className="font-serif text-4xl md:text-5xl mb-4">Unsubscribe from Bulk Buy Kits?</h1>
              <p className="text-stone leading-relaxed mb-8">
                One click and we'll stop emailing <span className="font-mono text-charcoal">{state.email}</span> about restocks, new kits, and sales. Order emails and tracking updates keep coming — those are transactional.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <button
                  onClick={confirm}
                  disabled={busy}
                  className="px-8 py-3 bg-oxblood text-bone tracking-[0.22em] text-xs uppercase rounded-full hover:bg-charcoal transition-colors disabled:opacity-50"
                  data-testid="unsub-confirm-btn"
                >
                  {busy ? "Unsubscribing…" : "Yes, unsubscribe"}
                </button>
                <Link
                  to="/shop"
                  className="px-6 py-3 border border-borderSubtle text-stone hover:text-charcoal hover:border-charcoal tracking-[0.22em] text-xs uppercase rounded-full transition-colors"
                  data-testid="unsub-cancel-link"
                >
                  Cancel
                </Link>
              </div>
            </>
          )}
        </div>
      </main>
      <footer className="border-t border-borderSubtle text-center py-6 px-6 text-[13px] font-mono uppercase tracking-widest text-stone">
        Bulk Buy Kits · 1617 17th Avenue South, Nashville, TN 37212
      </footer>
    </div>
  );
}
